import { SagaIterator } from 'redux-saga'
import { takeEvery, call, put } from 'redux-saga/effects'
import { Action } from 'typescript-fsa'
import { RedeemCode, RedeemCodeResponse } from 'mytours-read-api-typescript'

import { getConfiguration } from '../configuration/index'
import { store } from '../root/index'
import { AppMetaData } from '../api/types'
import { loadedMetaDataComplete } from '../settings/actions'
import {
	redeemBundleCoupon,
	validateBundleCoupon,
	fetchBundle,
	fetchInAppPurchases,
	RedeemBundleCouponResultSuccess,
} from '../api/actions'
import { removeRevokedCodeFromDisk, removeRejectedCodeFromDisk, revalidatePersistedCodes } from '../api/sagas'
import { expireAtFromHours } from '../api/functions'
import { MAX_BUNDLE_FETCH_ATTEMPTS } from './types'

/**
 * Persist a successfully-redeemed bundle code to disk (linkedType=Bundle) so the unlock survives
 * a reload — the bundles slice itself is blacklisted from redux-offline. Mirrors the lockapp
 * coupon-code disk pattern; the entry is rebuilt on boot by re-validating the persisted codes.
 */
function* handleRedeemBundleCouponDone(action: RedeemBundleCouponResultSuccess): SagaIterator {
	const { coupon, id } = action.payload.params
	const { status, expire_after } = action.payload.result
	if (status === RedeemCodeResponse.StatusEnum.Valid) {
		try {
			const projectId = getConfiguration().getDefaultAppInfo().projectId
			yield call(getConfiguration().saveCouponCodeToDisk, projectId, {
				code: coupon,
				linkedId: id,
				linkedType: RedeemCode.LinkedTypeEnum.Bundle,
				expireAt: expireAtFromHours(expire_after),
				addedAt: Date.now(),
			})
		} catch (error) {
			// Error saving data
		}
	}
}

/**
 * Whenever app metadata becomes available (boot — from disk or network — and every periodic
 * checkForUpdates sync) we (1) refresh the IAP catalogue so a bundle's items can be resolved and
 * reconciled, (2) replay any bundle fetched by id that failed, and (3) rebuild + re-validate any
 * redeemed bundles from their persisted disk codes. The bundles slice is wiped on reload (blacklisted
 * from redux-offline), so step 3 repopulates it; the optimistic-offline reducer keeps the unlock when
 * the network is down.
 *
 * Step 2 is the only retry those fetches get: the gate that asked keys its request on the bundle being
 * unknown, which a failure leaves unchanged. It runs every sync for the life of the session, so it is
 * bounded — a 4xx never replays, anything else until MAX_BUNDLE_FETCH_ATTEMPTS. Opening the gate again
 * dispatches a fresh fetch regardless.
 */
function* handleMetaDataReadyForBundles(action: Action<AppMetaData>): SagaIterator {
	const data = action.payload
	const version = data.updated_at
	const state = store.getState()
	const language = state.settings.selectedLanguage?.code || data.primary_language
	if (!version || !language) {
		return
	}

	const devicePlatform = getConfiguration().getPlatform()
	yield put(fetchInAppPurchases.started({ devicePlatform, version, language }))

	// Under the current language, not the one that failed.
	const failures = state.bundles.bundleFetchFailed ?? {}
	for (const failure of Object.values(failures)) {
		if (failure && !failure.permanent && failure.attempts < MAX_BUNDLE_FETCH_ATTEMPTS) {
			yield put(fetchBundle.started({ ...failure.params, language }))
		}
	}

	yield call(revalidatePersistedCodes, RedeemCode.LinkedTypeEnum.Bundle, validateBundleCoupon)
}

export default function* (): SagaIterator {
	yield takeEvery(redeemBundleCoupon.done, handleRedeemBundleCouponDone)
	yield takeEvery(validateBundleCoupon.done, removeRevokedCodeFromDisk, RedeemCode.LinkedTypeEnum.Bundle)
	yield takeEvery(validateBundleCoupon.failed, removeRejectedCodeFromDisk, RedeemCode.LinkedTypeEnum.Bundle)
	yield takeEvery(loadedMetaDataComplete, handleMetaDataReadyForBundles)
}
